import * as Sentry from "@sentry/browser";
import Environment from "utils/Environment";
import Tracker from "@openreplay/tracker";
import trackerRedux from "@openreplay/tracker-redux";

export default class OpenReplay {
  static tracker = null;

  static recordingRequest = null;

  static recordingContext = null;

  static starting = null;

  static getReduxMiddlewares() {
    if (!OpenReplay.tracker) return [];
    try {
      return [OpenReplay.tracker.use(trackerRedux({
        actionTransformer: OpenReplay.transformAction,
        stateTransformer: OpenReplay.transformState
      }))];
    } catch (error) {
      return [];
    }
  }

  static setSentryTags() {
    if (!OpenReplay.recordingRequest) return;
    try {
      const { sessionID: sessionId } = OpenReplay.tracker.getSessionInfo() || {};

      if (!sessionId) return;
      Sentry.setTag("openReplaySession.id", sessionId);
    } catch (error) {}
  }

  static init() {
    const { OPENREPLAY_PROJECT_KEY, OPENREPLAY_INGEST_POINT } = Environment.getProcessEnvVars();

    if ([OPENREPLAY_PROJECT_KEY, OPENREPLAY_INGEST_POINT].some((value) => !value || value === "null")) return;
    OpenReplay.tracker = new Tracker({
      projectKey: OPENREPLAY_PROJECT_KEY,
      ingestPoint: OPENREPLAY_INGEST_POINT,
      defaultInputMode: 0,
      obscureInputNumbers: false,
      obscureInputEmails: false,
      obscureTextEmails: false,
      network: { disabled: true },
      consoleMethods: [],
      captureExceptions: false,
      canvas: { disableCanvas: true },
      captureIFrames: false
    });
    OpenReplay.tracker.use(OpenReplay.connectSentry);
  }

  static async startRecording(request, userId, metadata) {
    OpenReplay.recordingContext = request.context;
    try {
      const startPromise = OpenReplay.tracker.start({
        userID: userId,
        metadata,
        forceNew: true
      });

      OpenReplay.starting = startPromise;
      await startPromise;
      if (OpenReplay.recordingRequest?.context !== request.context) OpenReplay.tracker.stop();
    } finally {
      OpenReplay.starting = null;
    }
  }

  static async start({
    userId,
    userEmail,
    userType,
    organizationId,
    organizationName,
    businessId,
    businessName,
    rpaBusiness
  } = {}) {
    if (!OpenReplay.tracker || !userId) return;

    const request = { context: JSON.stringify([userId, organizationId, businessId, userType, rpaBusiness]) };

    OpenReplay.recordingRequest = request;

    const metadata = {
      organizationName: organizationName || "",
      businessName: businessName || "",
      userType,
      rpaBusiness
    };

    try {
      if (OpenReplay.starting) await OpenReplay.starting;
      if (OpenReplay.recordingRequest !== request) return;
      if (OpenReplay.tracker.isActive() && OpenReplay.recordingContext !== request.context) OpenReplay.tracker.stop();
      if (OpenReplay.tracker.isActive()) {
        OpenReplay.tracker.setUserID(userEmail || userId);
        Object.entries(metadata).forEach(([name, value]) => OpenReplay.tracker.setMetadata(name, value));

        return;
      }
      await OpenReplay.startRecording(request, userEmail || userId, metadata);
    } catch (error) {}
  }

  static stop() {
    OpenReplay.recordingRequest = null;
    OpenReplay.recordingContext = null;
    try {
      if (OpenReplay.tracker) OpenReplay.tracker.stop();
    } catch (error) {}
    OpenReplay.clearSentryTags();
  }

  static transformAction({ type }) {
    return { type };
  }

  static transformState({ organizations, businesses, router }) {
    return {
      organizationId: organizations.data.find(({ active }) => active)?.id,
      businessId: businesses.selectedBusinessId,
      pathname: router.location.pathname
    };
  }

  static clearSentryTags() {
    try {
      Sentry.setTag("openReplaySession.id", undefined);
    } catch (error) {}
  }

  static connectSentry(app) {
    if (!app) return;
    app.attachStartCallback(OpenReplay.setSentryTags);
    app.attachStopCallback(OpenReplay.clearSentryTags);
  }
}
